// Amenities Module Types
import type { Unit } from '../residence';

export enum ReservationStatus {
  CONFIRMED = 'CONFIRMED',
  CANCELLED = 'CANCELLED',
  PENDING = 'PENDING',
  EXPIRED = 'EXPIRED',
}

export enum ChargeTiming {
  ON_REQUEST = 'ON_REQUEST',
  ON_CONFIRM = 'ON_CONFIRM',
}

/** Por hora (`precio × horas`) o total por reserva, sin importar las horas. */
export enum AmenityPricingMode {
  PER_HOUR = 'PER_HOUR',
  FLAT = 'FLAT',
}

/** Precio de la amenidad para las unidades de una sección. */
export interface AmenitySectionPrice {
  sectionId: string;
  pricingMode: AmenityPricingMode;
  price: number;
  /** `null` = usa el depósito de la amenidad; 0 = sin depósito. */
  depositAmount: number | null;
}

export interface WeeklyScheduleDay {
  isClosed: boolean;
  openTime: string;
  closeTime: string;
}

export interface BlackoutDate {
  date: string;
  reason?: string;
}

export interface CancellationPolicyTier {
  minHoursBefore: number;
  rentalRefundPercent: number;
  depositRefundPercent: number;
}

export interface CancellationPolicy {
  tiers: CancellationPolicyTier[];
}

export interface ReminderConfig {
  send24h?: boolean;
  send2h?: boolean;
  sendCustomHours?: number[];
}

export enum ReservationLimitPeriod {
  WEEK = 'WEEK',
  MONTH = 'MONTH',
}

export interface ReservationFrequencyLimit {
  maxReservations: number;
  period: ReservationLimitPeriod;
}

export interface Amenity {
  id: string;
  name: string;
  code?: string | null;
  capacity: number;
  imageUrl?: string | null;
  imageFileId?: string | null;
  requiresDeposit: boolean;
  depositAmount?: number | null;
  /** Precio base: por hora, o total por reserva si `pricingMode` es FLAT. */
  costPerHour: number;
  pricingMode?: AmenityPricingMode;
  /** Precios por sección; la sección sin fila usa el precio base. */
  sectionPrices?: AmenitySectionPrice[];
  minHours?: number | null;
  maxHours?: number | null;
  rules?: string | null;
  termsAndConditions?: string | null;
  isActive: boolean;
  chargeTiming?: ChargeTiming | null;
  requiresApproval?: boolean;
  autoConfirmIfPaid?: boolean;
  accessControlEnabled?: boolean;
  timezone?: string | null;
  weeklySchedule?: WeeklyScheduleDay[] | null;
  blackoutDates?: BlackoutDate[] | null;
  cancellationPolicy?: CancellationPolicy | null;
  reminderConfig?: ReminderConfig | null;
  accessControlDoorIds?: string[] | null;
  reservationFrequencyLimit?: ReservationFrequencyLimit | null;
  /** Secciones a las que pertenece; vacío = amenidad de todo el tenant. */
  sectionIds?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface Member {
  id: string;
  displayName: string;
  masterUserId?: string;
  unit?: Unit | null;
}

export interface ReservationAmenitySnapshot {
  id: string;
  name: string;
  code?: string | null;
  imageUrl?: string | null;
  timezone?: string | null;
}

export interface AmenityReservation {
  id: string;
  reservationCode?: string | null;
  amenityId: string;
  memberId: string;
  unitId?: string | null;
  memberName?: string;
  member: Member;
  amenity?: ReservationAmenitySnapshot;
  date: string;
  startTime: string;
  hours: number;
  totalCost: number;
  depositAmount: number;
  isRefunded: boolean;
  deductionAmount?: number | null;
  deductionReason?: string | null;
  status: ReservationStatus;
  serviceChargeId?: string | null;
  depositChargeId?: string | null;
  refundExpenseId?: string | null;
  notes?: string | null;
  createdBy?: string | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface ReservedSlot {
  id: string;
  startAt: string;
  endAt: string;
  startTime: string;
  endTime: string;
  hours: number;
  status: 'PENDING' | 'CONFIRMED';
}

export interface AmenityAvailability {
  amenityId: string;
  date: string;
  timezone: string;
  isClosed: boolean;
  isBlackout: boolean;
  openTime: string | null;
  closeTime: string | null;
  capacity: number;
  minHours: number;
  maxHours: number;
  reservedSlots: ReservedSlot[];
}
